import { useEffect, useRef } from 'react'
import type { Locale, Story, StoryCycle } from '../data/schema'
import { copy, localize } from '../i18n'
import { Icon } from './Icon'

interface StoryCatalogProps {
  locale: Locale
  cycles: StoryCycle[]
  stories: Story[]
  allowPreviewStories?: boolean
  onClose: () => void
  onOpenStory: (id: string) => void
}

export function StoryCatalog({ locale, cycles, stories, allowPreviewStories = false, onClose, onOpenStory }: StoryCatalogProps) {
  const t = copy[locale]
  const panelRef = useRef<HTMLElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    closeRef.current?.focus()
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const controls = [...(panelRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), [tabindex]:not([tabindex="-1"])') ?? [])]
      if (!controls.length) return
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      returnFocus?.focus()
    }
  }, [onClose])
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label={t.stories}>
      <div className="overlay-backdrop" onClick={onClose} aria-hidden="true" />
      <section className="catalog-panel" ref={panelRef}>
        <header>
          <div><span className="eyebrow">MYTH CYCLES · 神話系列</span><h2>{t.stories}</h2></div>
          <button ref={closeRef} className="icon-button" onClick={onClose} aria-label={t.close}><Icon name="close" /></button>
        </header>
        <p className="catalog-lead">{locale === 'en'
          ? 'Choose a myth cycle to find its stories and the people connected to them.'
          : '選一個神話系列，從相關故事和人物開始探索。'}</p>
        <div className="catalog-section-heading story-catalog-heading">
          <span className="eyebrow">STORY CYCLES · 故事系列</span>
          <h3>{locale === 'en' ? 'Story cycles' : '故事系列'}</h3>
        </div>
        <div className="cycle-grid">
          {cycles.map((cycle, cycleIndex) => (
            <article className="cycle-card" key={cycle.id} style={{ '--delay': `${cycleIndex * 70}ms` } as React.CSSProperties}>
              <span className="cycle-number">0{cycleIndex + 1}</span>
              <h3>{localize(cycle.name, locale)}</h3>
              <p>{localize(cycle.description, locale)}</p>
              <div className="story-list">
                {cycle.storyIds.map((storyId) => {
                  const story = stories.find((item) => item.id === storyId)
                  if (!story) return null
                  const available = story.status === 'available'
                  const preview = !available && allowPreviewStories && story.steps.length > 0
                  const readable = available || preview
                  return (
                    <button key={story.id} disabled={!readable} onClick={() => onOpenStory(story.id)}>
                      <span><strong>{localize(story.title, locale)}</strong><small>{available ? t.available : preview ? t.previewStory : t.comingSoon}</small></span>
                      {readable ? <Icon name="arrow-right" size={17} /> : <span className="soon-dot" />}
                    </button>
                  )
                })}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}
